import React, { useState, useEffect } from 'react'
import '../styles/Details.css'
import { Link } from 'react-router-dom'

import { EditOutlined, FileExcelOutlined } from '@ant-design/icons'
import { Table, Tag, Tooltip, Spin, Modal, Input, Descriptions, Button } from 'antd'
import { convertToTitleCase } from '../utils/stringUtils'
import { getAllProviders } from '../services/Clinician2Service'
import useDebounce from '../hooks/useDebounce'
import { categorizeAppointment } from '../utils/categorizeAppointment'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'

const { Search } = Input
const { Column } = Table

function Clinicians2Details() {
  const [currentPage, setCurrentPage] = useState(1)
  const [pageLimit, setPageLimit] = useState(10)
  const [totalItems, setTotalItems] = useState(1)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalTitle, setModalTitle] = useState('')
  const [modalComponent, setModalComponent] = useState()
  const [data, setData] = useState([])
  const [allCliniciansData, setAllCliniciansData] = useState([])
  const [searchField, setSearchField] = useState('')
  const [clinicianDetailLoading, setClinicianDetailLoading] = useState(false)

  const [loading, setLoading] = useState(false)

  const columnsMapping = {
    'id': 'ID',
    'first_name': 'First Name',
    'last_name': 'Last Name',
    'email': 'Email',
    'phone': 'Phone',
    'provider_title': 'Provider Title',
    'npi_number': 'NPI Number',
    'gender': 'Gender',
    'dob': 'Date of Birth',
    'city': 'City',
    'state': 'State',
    'postal_code': 'Postal Code',
    'country': 'Country',
    'timezone': 'Time Zone',
    'doxy_link': 'Doxy Link',
    'provider_bio': 'Provider Bio',
    'available_for_scheduling': 'Available for Scheduling',
    'available_for_new_booking': 'Available for New Booking',
    'available_for_followup_booking': 'Available for Follow-up Booking',
    'search_priority': 'Search Priority',
    'specialisation': 'Specialization',
    'age_preferences': 'Age Preferences',
    'language_preferences': 'Language Preferences',
    'treatment_preferences': 'Treatment Preferences',
    'is_active': 'Status',
    'created_at': 'Created Date',
    'appointment_types': 'Appointment Types',
    'amd_column_id': 'AMD Column ID',
    'amd_column_heading': 'AMD Column Heading',
    'ghl_user_id': 'GHL User ID',
    'ghl_calendar_id': 'GHL Calendar ID',
    'ghl_contact_id': 'GHL Contact ID'
  };

  const generateExcel = (data) => {
    // Sort data by ID before processing
    const sortedData = [...data].sort((a, b) => (a.id || 0) - (b.id || 0))
    
    const transformedData = sortedData.map((clinician) => {
      const excelRow = {}
      
      // Basic info
      excelRow[columnsMapping['id']] = clinician.id || ''
      excelRow[columnsMapping['first_name']] = clinician.first_name || ''
      excelRow[columnsMapping['last_name']] = clinician.last_name || ''
      excelRow[columnsMapping['email']] = clinician.email || ''
      excelRow[columnsMapping['phone']] = clinician.phone || ''
      excelRow[columnsMapping['provider_title']] = clinician.provider_title || ''
      excelRow[columnsMapping['npi_number']] = clinician.npi_number || ''
      excelRow[columnsMapping['gender']] = clinician.gender || ''
      excelRow[columnsMapping['dob']] = clinician.dob || ''
      
      // Location
      excelRow[columnsMapping['city']] = clinician.city || ''
      excelRow[columnsMapping['state']] = clinician.state || ''
      excelRow[columnsMapping['postal_code']] = clinician.postal_code || ''
      excelRow[columnsMapping['country']] = clinician.country || ''
      excelRow[columnsMapping['timezone']] = clinician.timezone || ''
      
      // Professional info
      excelRow[columnsMapping['doxy_link']] = clinician.doxy_link || ''
      excelRow[columnsMapping['provider_bio']] = clinician.provider_bio || ''
      
      // Availability
      excelRow[columnsMapping['available_for_scheduling']] = clinician.available_for_scheduling ? 'Yes' : 'No'
      excelRow[columnsMapping['available_for_new_booking']] = clinician.available_for_new_booking ? 'Yes' : 'No'
      excelRow[columnsMapping['available_for_followup_booking']] = clinician.available_for_followup_booking ? 'Yes' : 'No'
      excelRow[columnsMapping['search_priority']] = clinician.search_priority || ''
      
      // Preferences and specializations (convert arrays to comma-separated strings)
      excelRow[columnsMapping['specialisation']] = Array.isArray(clinician.specialisation) 
        ? clinician.specialisation.join(', ') 
        : (clinician.specialisation || '')
      excelRow[columnsMapping['age_preferences']] = Array.isArray(clinician.age_preferences) 
        ? clinician.age_preferences.join(', ') 
        : (clinician.age_preferences || '')
      excelRow[columnsMapping['language_preferences']] = Array.isArray(clinician.language_preferences) 
        ? clinician.language_preferences.join(', ') 
        : (clinician.language_preferences || '')
      excelRow[columnsMapping['treatment_preferences']] = Array.isArray(clinician.treatment_preferences) 
        ? clinician.treatment_preferences.join(', ') 
        : (clinician.treatment_preferences || '')
      
      // Status and dates
      excelRow[columnsMapping['is_active']] = clinician.is_active ? 'Active' : 'Inactive'
      excelRow[columnsMapping['created_at']] = clinician.created_at || ''
      
      // Appointment types (remove duplicates)
      excelRow[columnsMapping['appointment_types']] = Array.isArray(clinician.appt_types) 
        ? [...new Set(clinician.appt_types.map(apptType => categorizeAppointment(apptType.title)))].join(', ')
        : ''
      
      // Integration mappings
      if (clinician.provider_integration_map) {
        excelRow[columnsMapping['amd_column_id']] = clinician.provider_integration_map.amd_column_id || ''
        excelRow[columnsMapping['amd_column_heading']] = clinician.provider_integration_map.amd_column_heading || ''
        excelRow[columnsMapping['ghl_user_id']] = clinician.provider_integration_map.ghl_user_id || ''
        excelRow[columnsMapping['ghl_calendar_id']] = clinician.provider_integration_map.ghl_calendar_id || ''
        excelRow[columnsMapping['ghl_contact_id']] = clinician.provider_integration_map.ghl_contact_id || ''
      } else {
        excelRow[columnsMapping['amd_column_id']] = ''
        excelRow[columnsMapping['amd_column_heading']] = ''
        excelRow[columnsMapping['ghl_user_id']] = ''
        excelRow[columnsMapping['ghl_calendar_id']] = ''
        excelRow[columnsMapping['ghl_contact_id']] = ''
      }
      
      return excelRow
    })

    // Create worksheet from JSON data
    const ws = XLSX.utils.json_to_sheet(transformedData)
    
    // Set column widths
    const columnWidths = []
    Object.values(columnsMapping).forEach(header => {
      columnWidths.push({ wch: Math.max(header.length * 1.5, 15) })
    })
    ws['!cols'] = columnWidths
    
    // Create workbook and add the worksheet
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Clinicians')
    
    // Generate Excel file as array buffer
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
    
    // Create Blob for saving
    return new Blob([excelBuffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' 
    })
  }

  const handleExportToExcel = () => {
    try {
      if (!allCliniciansData || allCliniciansData.length === 0) {
        Modal.warning({
          title: 'No Data',
          content: 'No clinician data available to export. Please wait for data to load.'
        })
        return
      }

      const blobData = generateExcel(allCliniciansData)
      saveAs(blobData, `Clinicians_${new Date().toISOString().split('T')[0]}.xlsx`)
    } catch (error) {
      console.error('Error in export process:', error)
      Modal.error({
        title: 'Export Error',
        content: 'Failed to export data to Excel. Please try again.'
      })
    }
  }

  const handleTableChange = (pagination, filters) => {
    setLoading(true)
    setCurrentPage(pagination.current)
    setPageLimit(pagination.pageSize)
    setLoading(false)
  }

  const handleOk = () => {
    setIsModalOpen(false)
  }

  const handleCancel = () => {
    setIsModalOpen(false)
  }

  const searchProviders = async (filters = {}) => {
    try {
      setLoading(true)
      // Extract the searchField for client-side filtering
      const searchField = filters.searchField;
      
      // If there's a searchField, get all providers and filter client-side
      if (searchField) {
        const searchTerm = searchField.toLowerCase();
        
        return allCliniciansData?.filter(provider => 
          (provider.first_name && provider.first_name.toLowerCase().includes(searchTerm)) ||
          (provider.last_name && provider.last_name.toLowerCase().includes(searchTerm)) ||
          (provider.email && provider.email.toLowerCase().includes(searchTerm)) ||
          (provider.phone && provider.phone.toLowerCase().includes(searchTerm))
        );
      }
  
      return []
    } catch (error) {
      console.error('Error searching providers:', error);
      throw error;
    } finally {
      setLoading(false)
    }
  };

  async function loadData(filters = {}) {
    try {
      setLoading(true)
      
      // Use search endpoint if filters are provided, otherwise get all providers
      let providerResponse;
      if (Object.keys(filters).length > 0) {
        providerResponse = await searchProviders(filters);
      } else {
        providerResponse = await getAllProviders();
      }

      setTotalItems(providerResponse?.length || 0)
      if (providerResponse?.length) {
        setAllCliniciansData(providerResponse)
        setData(
          providerResponse
            .map((element) => {
              return {
                key: element.id,
                id: element.id,
                clinician: element.first_name && element.last_name
                  ? convertToTitleCase(element.first_name) + ' ' + convertToTitleCase(element.last_name)
                  : '',
                email: element.email ? element.email.toLowerCase() : '',
                phone: element.phone || '',
                clinicianTitle: element.provider_title || '',
                clinicianStatus: element.is_active,
                createdAt: element.created_at,
                doxyLink: element.doxy_link || '',
                availableForScheduling: element.available_for_scheduling ? 'Yes' : 'No',
                timezone: element.timezone || '',
                languagePreferences: element.language_preferences ? element.language_preferences : [],
                specialization: element.specialisation ? element.specialisation : [],
                agePreferences: element.age_preferences ? element.age_preferences : [],
                treatmentPreferences: element.treatment_preferences ? element.treatment_preferences : [],
                clinicianBio: element.provider_bio || ''
              }
            })
            .sort((a, b) => a.clinician.localeCompare(b.clinician))
        )
      }
    } catch (e) {
      console.log('failed to load data from database', e)
      Modal.confirm({
        title: 'Error Message',
        content: 'Oops! Error in Fetching data',
        okText: 'Ok',
        cancelButtonProps: { style: { display: 'none' } }
      })
    } finally {
      setLoading(false) // Reset loading once data fetching is complete
    }
    return true
  }

  const debouncedSearchValue = useDebounce(searchField)

  const handleSearch = (value) => {
    setSearchField(value)
  }

  useEffect(() => {
    setData([])
    data.length = 0

    const filters = {}
    
    if (debouncedSearchValue.length !== 0) {
      filters['searchField'] = debouncedSearchValue
    }
    loadData(filters)
  }, [
    debouncedSearchValue
  ])

  const showClinicianDetails = async (id) => {
    try {
      setClinicianDetailLoading(true)
      setIsModalOpen(true)
      setModalTitle('Clinician Details')
      
      // Fetch complete clinician data
      const clinician = allCliniciansData.find(clinician => clinician.id === id)
      
      // Create modal content with the clinician details
      setModalComponent(
        <Spin spinning={clinicianDetailLoading}>
          {clinician && (
            <div style={{ maxHeight: '70vh', overflowY: 'auto', padding: '10px' }}>
              <Descriptions bordered column={1} size="small">
                <Descriptions.Item label="Profile Image" labelStyle={{ fontWeight: 'bold' }}>
                    {clinician.profile_image_signed_url && (
                      <div 
                        key={`image-container-${clinician.id}`}
                        style={{
                        height: '95px',
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        borderRadius: '4px'
                      }}>
                        <Spin spinning={true} size="small" style={{ position: 'absolute' }}>
                          <div style={{ width: '30px', height: '30px' }}></div>
                        </Spin>
                        <img 
                          key={`clinician-image-${clinician.id}`}
                          src={clinician.profile_image_signed_url}
                          alt="Clinician profile" 
                          style={{ 
                            maxWidth: '100%', 
                            maxHeight: '95px', 
                            borderRadius: '4px',
                            display: 'none'
                          }}
                          onLoad={(e) => {
                            e.target.style.display = 'block';
                            e.target.previousSibling.style.display = 'none';
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.previousSibling.style.display = 'none';
                            e.target.parentElement.innerHTML = 'Image failed to load';
                          }}
                        />
                      </div>
                    )}
                    {!clinician.profile_image_signed_url && (
                      <div style={{ 
                        height: '95px', 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center',
                        background: '#f9f9f9',
                        borderRadius: '4px',
                        border: '1px solid #d9d9d9'
                      }}>
                        No image available
                      </div>
                    )}
                </Descriptions.Item>
                <Descriptions.Item label="Name" labelStyle={{ fontWeight: 'bold' }}>
                  {clinician.first_name && clinician.last_name 
                    ? `${convertToTitleCase(clinician.first_name)} ${convertToTitleCase(clinician.last_name)}`
                    : 'Not Available'}
                </Descriptions.Item>
                <Descriptions.Item label="Email" labelStyle={{ fontWeight: 'bold' }}>{clinician.email || 'Not Available'}</Descriptions.Item>
                <Descriptions.Item label="Phone" labelStyle={{ fontWeight: 'bold' }}>{clinician.phone || 'Not Available'}</Descriptions.Item>
                <Descriptions.Item label="Provider Title" labelStyle={{ fontWeight: 'bold' }}>{clinician.provider_title || 'Not Available'}</Descriptions.Item>
                <Descriptions.Item label="NPI Number" labelStyle={{ fontWeight: 'bold' }}>{clinician.npi_number || 'Not Available'}</Descriptions.Item>
                <Descriptions.Item label="Gender" labelStyle={{ fontWeight: 'bold' }}>{clinician.gender || 'Not Available'}</Descriptions.Item>
                <Descriptions.Item label="Date of Birth" labelStyle={{ fontWeight: 'bold' }}>{clinician.dob || 'Not Available'}</Descriptions.Item>
                <Descriptions.Item label="Location" labelStyle={{ fontWeight: 'bold' }}>
                  {`${clinician.city || ''} ${clinician.state || ''} ${clinician.postal_code || ''} ${clinician.country || ''}`.trim() || 'Not Available'}
                </Descriptions.Item>
                <Descriptions.Item label="Time Zone" labelStyle={{ fontWeight: 'bold' }}>{clinician.timezone || 'Not Available'}</Descriptions.Item>
                <Descriptions.Item label="Doxy Link" labelStyle={{ fontWeight: 'bold' }}>
                  {clinician.doxy_link ? (
                    <a href={clinician.doxy_link} target="_blank" rel="noreferrer">
                      {clinician.doxy_link}
                    </a>
                  ) : (
                    'Not Available'
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Provider Bio" labelStyle={{ fontWeight: 'bold' }}>{clinician.provider_bio || 'Not Available'}</Descriptions.Item>
                <Descriptions.Item label="Appointment Types" labelStyle={{ fontWeight: 'bold' }}>
                  {clinician.appt_types ? (
                    [...new Set(clinician.appt_types.map(apptType => categorizeAppointment(apptType.title)))]
                      .map((categorizedTitle, index) => (
                        <Tag color="blue" key={index} style={{ margin: '2px' }}>{categorizedTitle}</Tag>
                      ))
                  ) : (
                    'Not Available'
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Available for Scheduling" labelStyle={{ fontWeight: 'bold' }}>
                  {clinician.available_for_scheduling ? (
                    <Tag color="green">Yes</Tag>
                  ) : (
                    <Tag color="red">No</Tag>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Available for New Booking" labelStyle={{ fontWeight: 'bold' }}>
                  {clinician.available_for_new_booking ? (
                    <Tag color="green">Yes</Tag>
                  ) : (
                    <Tag color="red">No</Tag>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Available for Follow-up Booking" labelStyle={{ fontWeight: 'bold' }}>
                  {clinician.available_for_followup_booking ? (
                    <Tag color="green">Yes</Tag>
                  ) : (
                    <Tag color="red">No</Tag>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Search Priority" labelStyle={{ fontWeight: 'bold' }}>{clinician.search_priority || 'Not Available'}</Descriptions.Item>
                <Descriptions.Item label="Specialization" labelStyle={{ fontWeight: 'bold' }}>
                  {clinician.specialisation && clinician.specialisation.length > 0 ? (
                    clinician.specialisation.map(spec => (
                      <Tag color="blue" key={spec} style={{ margin: '2px' }}>{spec}</Tag>
                    ))
                  ) : (
                    'Not Available'
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Age Preferences" labelStyle={{ fontWeight: 'bold' }}>
                  {clinician.age_preferences && clinician.age_preferences.length > 0 ? (
                    clinician.age_preferences.map(age => (
                      <Tag color="blue" key={age} style={{ margin: '2px' }}>{age}</Tag>
                    ))
                  ) : (
                    'Not Available'
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Language Preferences" labelStyle={{ fontWeight: 'bold' }}>
                  {clinician.language_preferences && clinician.language_preferences.length > 0 ? (
                    clinician.language_preferences.map(lang => (
                      <Tag color="blue" key={lang} style={{ margin: '2px' }}>{lang}</Tag>
                    ))
                  ) : (
                    'Not Available'
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Treatment Preferences" labelStyle={{ fontWeight: 'bold' }}>
                  {clinician.treatment_preferences && clinician.treatment_preferences.length > 0 ? (
                    clinician.treatment_preferences.map(treatment => (
                      <Tag color="blue" key={treatment} style={{ margin: '2px' }}>{treatment}</Tag>
                    ))
                  ) : (
                    'Not Available'
                  )}
                </Descriptions.Item>
                {clinician.provider_integration_map && (
                  <>
                    <Descriptions.Item label="AMD Column ID" labelStyle={{ fontWeight: 'bold' }}>
                      { clinician.provider_integration_map.amd_column_id || 'Not Available'}
                    </Descriptions.Item>
                    <Descriptions.Item label="AMD Column Heading" labelStyle={{ fontWeight: 'bold' }}>
                      {clinician.provider_integration_map.amd_column_heading || 'Not Available'}
                    </Descriptions.Item>
                    <Descriptions.Item label="GHL User ID" labelStyle={{ fontWeight: 'bold' }}>
                      {clinician.provider_integration_map.ghl_user_id || 'Not Available'}
                    </Descriptions.Item>
                    <Descriptions.Item label="GHL Calendar ID" labelStyle={{ fontWeight: 'bold' }}>
                      {clinician.provider_integration_map.ghl_calendar_id || 'Not Available'}
                    </Descriptions.Item>
                    <Descriptions.Item label="GHL Contact ID" labelStyle={{ fontWeight: 'bold' }}>
                      {clinician.provider_integration_map.ghl_contact_id || 'Not Available'}
                    </Descriptions.Item>
                  </>
                )}
              </Descriptions>
            </div>
          )}
        </Spin>
      )
    } catch (error) {
      console.error('Error fetching clinician details:', error)
      Modal.error({
        title: 'Error',
        content: 'Failed to load clinician details. Please try again later.'
      })
    } finally {
      setClinicianDetailLoading(false)
    }
  }

  return (
    <div id="dashboard-container">
      <div className="row styleFilterRow">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontWeight: 500 }}>Total Clinicians</span>
            <Tag>
              <strong style={{ fontSize: 16 }}>{data.length === 0 ? 0 : totalItems}</strong>
            </Tag>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Tooltip title="Export to Excel">
              <Button 
                icon={<FileExcelOutlined />} 
                onClick={handleExportToExcel}
                disabled={loading || !allCliniciansData || allCliniciansData.length === 0}
              >
                Export
              </Button>
            </Tooltip>
            
            <div style={{ width: '400px' }}>
              <Search
                id="search"
                placeholder="Search Clinician by Name, Email, or Phone"
                value={searchField}
                onChange={(e) => setSearchField(e.target.value)}
                onSearch={handleSearch}
                allowClear
                disabled={loading}
              />
            </div>
          </div>
        </div>
      </div>

      <div style={{ marginBottom: 5 }}>
        <span style={{ fontWeight: 500 }}>Click on Clinician name to view more details.</span>
      </div>
      <Spin spinning={loading}>
        <Table
          dataSource={data}
          pagination={{
            current: currentPage,
            total: totalItems,
            pageSize: pageLimit,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            onChange: (page, pageSize) => {
              setCurrentPage(page);
              setPageLimit(pageSize);
            }
          }}
          bordered={true}
          onChange={handleTableChange}
        >
          <Column
            title="Name"
            key="clinicianCombined"
            render={(record) => (
              <div>
                <strong 
                  onClick={() => showClinicianDetails(record.id)}
                  style={{ cursor: 'pointer', color: '#1890ff' }}
                >
                  {record.clinician}
                </strong>
                <div>{record.email}</div>
                <div>{record.phone}</div>
              </div>
            )}
          />

          <Column
            title="Specialization"
            key="specialization"
            render={(record) => (
              <>
                {record.specialization ? (
                  record?.specialization?.sort()?.map((specialization) => (
                    <span key={specialization}>
                      <Tag color="blue" style={{ margin: '2px' }}>
                        {specialization}
                      </Tag>
                    </span>
                  ))
                ) : (
                  <Tag color="red">Not Available</Tag>
                )}
              </>
            )}
          />

          <Column
            title="Age Preferences"
            key="agePreferences"
            render={(record) => (
              <>
                {record.agePreferences ? (
                  record?.agePreferences?.map((agepoint) => (
                    <span key={agepoint}>
                      <Tag color="blue" style={{ margin: '2px' }}>
                        {agepoint}
                      </Tag>
                    </span>
                  ))
                ) : (
                  <Tag color="red">Not Available</Tag>
                )}
              </>
            )}
          />

          <Column
            title="Treatment Preferences"
            key="treatmentPreferences"
            render={(record) => (
              <>
                {record.treatmentPreferences ? (
                  record.treatmentPreferences.map((treatment) => (
                    <span key={treatment}>
                      <Tag color="blue" style={{ margin: '2px' }}>
                        {treatment}
                      </Tag>
                      <br />
                    </span>
                  ))
                ) : (
                  <Tag color="red">Not Available</Tag>
                )}
              </>
            )}
          />

          <Column 
            title="Time zone" 
            dataIndex="timezone" 
            key="timezone" 
            render={(timezone) => (
             <>
              {timezone ? (
                <div>{timezone}</div>
              ) : (
                <Tag color="red">Not Available</Tag>
              )}
             </>
            )}
          />

          <Column
            title="Language Preferences"
            key="languagePreferences"
            render={(record) => (
              <>
                {record.languagePreferences ? (
                  record?.languagePreferences?.sort()?.map((language) => (
                    <span key={language}>
                      <Tag color="blue" style={{ margin: '2px' }}>
                        {language}
                      </Tag>
                    </span>
                  ))
                ) : (
                  <Tag color="red">Not Available</Tag>
                )}
              </>
            )}
          />
          <Column
            title="Doxy Link"
            dataIndex="doxyLink"
            key="doxyLink"
            render={(doxyLink) => (
              <>
                {doxyLink ? (
                  <a href={doxyLink} target="_blank" rel="noreferrer">
                    Link
                  </a>
                ) : (
                  <Tag color="red">Not Available</Tag>
                )}
              </>
            )}
          />
          <Column
            title="Available for Scheduling"
            dataIndex="availableForScheduling"
            key="availableForScheduling"
            render={(bookingStatus) => (
              <>
                {bookingStatus === 'Yes' ? (
                  <Tag color="green">Yes</Tag>
                ) : (
                  <Tag color="red">No</Tag>
                )}
              </>
            )}
          />

          <Column
            title="Action"
            dataIndex="id"
            key="id"
            render={(id) => (
              <>
                <Tooltip title={'Complete Onboarding'}>
                  <Link to={`/complete-clinician-onboarding?id=${id}`} style={{ color: 'black', textDecoration: 'none' }}>
                    <EditOutlined style={{ marginRight: '10px', cursor: 'pointer' }} />
                  </Link>
                </Tooltip>
              </>
            )}
          />
        </Table>
      </Spin>
      <Modal
        title={modalTitle}
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        footer={null}
        maskClosable={true}
        centered={true}
        width={700}
        style={{ minHeight: '300px', fontSize: '1rem' }}
      >
        {modalComponent}
      </Modal>
    </div>
  )
}

export default Clinicians2Details
